'use client';

import React, { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/navigation';
import { Plus } from 'lucide-react';
import type { ShellTokens } from '@/lib/shell/tokens';

export type ShellCreateItem = {
  id: string;
  label: string;
  hint?: string;
  href?: string;
  onSelect?: () => void;
  icon?: ReactNode;
};

const MENU_WIDTH = 224;

/**
 * The top-bar Create menu. Every item must carry a real destination (href) or a
 * real handler (onSelect) — items are navigation into existing creation flows,
 * never placeholder entries.
 */
export default function ShellCreateMenu({
  tokens,
  label,
  items,
}: {
  tokens: ShellTokens;
  label: string;
  items: ShellCreateItem[];
}) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = useCallback((restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !triggerRef.current?.contains(event.target as Node)
      ) {
        close();
      }
    };
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') close(true);
    };
    document.addEventListener('mousedown', handleOutside, true);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [close, open]);

  // Arrow-key navigation between menu items.
  const handleMenuKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!menuRef.current) return;
    const buttons = Array.from(
      menuRef.current.querySelectorAll<HTMLButtonElement>('[role="menuitem"]')
    );
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      buttons[(index + 1) % buttons.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      buttons[(index - 1 + buttons.length) % buttons.length]?.focus();
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
  }, [open]);

  if (items.length === 0) return null;

  let left = 0;
  let top = 0;
  if (anchorRect && typeof window !== 'undefined') {
    left = Math.min(Math.max(anchorRect.right - MENU_WIDTH, 8), window.innerWidth - MENU_WIDTH - 8);
    top = anchorRect.bottom + 4;
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => {
          setAnchorRect(triggerRef.current?.getBoundingClientRect() ?? null);
          setOpen((value) => !value);
        }}
        // Hidden below 640px so it doesn't compete with the title block for
        // space on very narrow phones. `flex` (not an inline display style) so
        // the sm:flex override -- same specificity, later in the stylesheet --
        // can actually take effect; an inline display would always win.
        className="hidden sm:flex shell-transition shell-hit-target"
        style={{
          alignItems: 'center',
          gap: 6,
          height: 32,
          padding: '0 10px',
          borderRadius: 8,
          border: `1px solid ${tokens.activeBorder}`,
          background: tokens.accentSoft,
          color: tokens.accentText,
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <Plus size={14} aria-hidden="true" />
        <span className="hidden lg:inline">{label}</span>
      </button>

      {open && anchorRect
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              aria-label={label}
              onKeyDown={handleMenuKeyDown}
              style={{
                position: 'fixed',
                top,
                left,
                width: MENU_WIDTH,
                borderRadius: 12,
                background: tokens.surface,
                border: `1px solid ${tokens.border}`,
                boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
                zIndex: 99999,
                overflow: 'hidden',
                padding: '6px 0',
              }}
            >
              {items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    close(true);
                    item.onSelect?.();
                    if (item.href) router.push(item.href);
                  }}
                  className="shell-transition"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: tokens.textPrimary,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.background = tokens.surfaceHover;
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.background = 'transparent';
                  }}
                >
                  {item.icon ? (
                    <span style={{ display: 'flex', flexShrink: 0, color: tokens.textMuted }}>
                      {item.icon}
                    </span>
                  ) : null}
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'block' }}>{item.label}</span>
                    {item.hint ? (
                      <span style={{ display: 'block', fontSize: 10.5, color: tokens.textMuted }}>
                        {item.hint}
                      </span>
                    ) : null}
                  </span>
                </button>
              ))}
            </div>,
            document.body
          )
        : null}
    </>
  );
}
